import { CheckOutlined, DownOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import React from 'react';

/**
 * A short, coloured value shown as a pill and changed from a menu under it.
 *
 * For a value that is read far more often than it is changed - a classification,
 * a kind, a tier - where a row of closed selects would turn a list into a form.
 * The pill stays put while the menu is open, so nothing beside it moves.
 */

export type ChipOption<V extends string> = {
  value: V;
  label: string;
  /** Chip text and, unless `icon` is given, its dot. */
  color: string;
  /** Chip fill. */
  bg: string;
  /** Chip border. Defaults to a soft tint of `color`. */
  border?: string;
  /** Replaces the dot where a picture tells the values apart better. */
  icon?: React.ReactNode;
};

type ChipSelectProps<V extends string> = {
  value?: V | null;
  options: ReadonlyArray<ChipOption<V>>;
  onChange: (value: V | null) => void;
  /** Chip text while nothing is set. Name the action, not the empty state. */
  addLabel: string;
  /** The menu entry that unsets the value. */
  clearLabel: string;
  /** Accessible name. The chip already reads its own label, so it carries no tooltip. */
  title: string;
  /** True while a change is in flight: the chip holds its place and stops taking clicks. */
  saving?: boolean;
  disabled?: boolean;
  className?: string;
};

const CLEAR_KEY = '__clear__';

const Glyph = <V extends string>({ option }: { option: ChipOption<V> }) =>
  option.icon ?? <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: option.color }} />;

export function ChipSelect<V extends string>({
  value,
  options,
  onChange,
  addLabel,
  clearLabel,
  title,
  saving,
  disabled,
  className,
}: ChipSelectProps<V>) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((option) => option.value === value) ?? null;

  const items: MenuProps['items'] = [
    ...options.map((option) => ({
      key: option.value,
      label: (
        <span className="flex items-center gap-2 pr-1">
          <Glyph option={option} />
          <span className="flex-1">{option.label}</span>
          {option.value === selected?.value && (
            <CheckOutlined className="text-[11px]" style={{ color: option.color }} />
          )}
        </span>
      ),
    })),
    ...(selected
      ? [
          { type: 'divider' as const, key: 'clear-divider' },
          { key: CLEAR_KEY, label: <span className="text-slate-500">{clearLabel}</span> },
        ]
      : []),
  ];

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    setOpen(false);
    if (key === CLEAR_KEY) {
      onChange(null);
      return;
    }
    // A no-op pick is a click, not a change: saving it would cost a request and
    // a toast to write back what is already there.
    if (key !== selected?.value) onChange(key as V);
  };

  const border = selected ? (selected.border ?? `${selected.color}33`) : undefined;

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      disabled={disabled || saving}
      menu={{ items, onClick: onMenuClick }}
      placement="bottomLeft"
    >
      <button
        // `type` matters: these live inside the visit and client forms, where a
        // bare button submits the form.
        type="button"
        aria-label={title}
        disabled={disabled || saving}
        className={
          'inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors disabled:cursor-default ' +
          (selected
            ? ''
            : 'border-dashed border-slate-300 text-slate-400 hover:border-slate-400 hover:text-slate-600 ') +
          (className ?? '')
        }
        style={
          selected
            ? {
                color: selected.color,
                background: selected.bg,
                borderColor: border,
                boxShadow: open ? `0 0 0 2px ${selected.color}22` : undefined,
              }
            : undefined
        }
      >
        <span className="flex text-[11px] leading-none">
          {saving ? <LoadingOutlined /> : selected ? <Glyph option={selected} /> : <PlusOutlined />}
        </span>
        <span className="leading-none">{selected ? selected.label : addLabel}</span>
        {!saving && <DownOutlined className="text-[9px] leading-none opacity-70" />}
      </button>
    </Dropdown>
  );
}

export default ChipSelect;
